/**
 * DeviceDetailPanel Premium - Best of Both Worlds
 * Layout do original + Visual premium (gradients, glassmorphism)
 */

import React, { memo, useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  Divider,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { alpha, useTheme } from '@mui/material/styles';
import { usePremiumTokens } from '../theme/usePremiumTokens';
import { typography, radius, transitions } from '../theme/premiumTokens';
import {
  Close,
  MyLocation,
  StreetviewOutlined,
  Send,
  Lock,
  LockOpen,
  Edit,
  Delete,
  Speed,
  SatelliteAlt,
  Battery20,
  Battery50,
  Battery80,
  BatteryFull,
  Anchor,
  Power,
  SignalCellularAlt,
  DirectionsCar,
  Route,
  CameraAlt,
  MoreVert,
  Map,
  Visibility,
  PersonAdd,
  Info,
  AddLocationAlt,
  NotificationsActive,
  LocalGasStation,
  Thermostat,
  Person,
  Explore,
  Timer,
  Terrain,
  Navigation, // 2ECOMP: follow popup
} from '@mui/icons-material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCarBattery, faTachometerAlt, faHourglassHalf, faGaugeHigh } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from './LocalizationProvider';
import { formatSpeed, formatAlarm } from '../util/formatter';
import { useAttributePreference } from '../util/preferences';
import { useAdministrator, useManager, useDeviceReadonly, useLimitCommand, useRestriction } from '../util/permissions';
import { useSelector, useDispatch } from 'react-redux';
import { devicesActions } from '../../store'; // 2ECOMP: follow popup
import PositionValue from './PositionValue';
import { mapIconKey, mapIcons } from '../../map/core/preloadImages';
import { icons, iconsRemote } from '../../map/core/icons3d/index.js';
import usePositionAttributes from '../attributes/usePositionAttributes';
import { getPowerVoltage } from '../util/deviceColors';

// Realistic device images
import realisticBicycle from '../../resources/images/icon/realistic/bicycle.webp';
import realisticBoat from '../../resources/images/icon/realistic/boat.webp';
import realisticBus from '../../resources/images/icon/realistic/bus.webp';
import realisticCamper from '../../resources/images/icon/realistic/camper.webp';
import realisticCar from '../../resources/images/icon/realistic/car.webp';
import realisticCrane from '../../resources/images/icon/realistic/crane.webp';
import realisticHelicopter from '../../resources/images/icon/realistic/helicopter.webp';
import realisticMotorcycle from '../../resources/images/icon/realistic/motorcycle.webp';
import realisticOffroad from '../../resources/images/icon/realistic/off-road.webp';
import realisticPickup from '../../resources/images/icon/realistic/pickup.webp';
import realisticPlane from '../../resources/images/icon/realistic/plane.webp';
import realisticScooter from '../../resources/images/icon/realistic/scooter.webp';
import realisticShip from '../../resources/images/icon/realistic/ship.webp';
import realisticSuv from '../../resources/images/icon/realistic/suv.webp';
import realisticTractor from '../../resources/images/icon/realistic/tractor.webp';
import realisticTrailer from '../../resources/images/icon/realistic/trailer.webp';
import realisticTrain from '../../resources/images/icon/realistic/train.webp';
import realisticTruck from '../../resources/images/icon/realistic/truck.webp';
import realisticVan from '../../resources/images/icon/realistic/van.webp';

const realisticImages: Record<string, string> = {
  bicycle: realisticBicycle, boat: realisticBoat, bus: realisticBus, camper: realisticCamper,
  car: realisticCar, crane: realisticCrane, helicopter: realisticHelicopter, motorcycle: realisticMotorcycle,
  offroad: realisticOffroad, pickup: realisticPickup, plane: realisticPlane, scooter: realisticScooter,
  ship: realisticShip, suv: realisticSuv, tractor: realisticTractor, trailer: realisticTrailer,
  train: realisticTrain, tram: realisticTrain, truck: realisticTruck, van: realisticVan, default: realisticCar,
};

const getRealisticImage = (category?: string): string => {
  if (!category) return realisticImages.default;
  return realisticImages[category.toLowerCase()] || realisticImages.default;
};

// Icon helpers
const getIcon3dUrl = (category: string | undefined): string | null => {
  const cat = (category || 'default').toLowerCase();
  const iconName = (iconsRemote as Record<string, string>)[cat];
  if (!iconName) return null;
  // Usa cor default do ícone (sem passar parâmetro de cor)
  return `/icons3d/${iconName}.php?grados=90`;
};

const getLocalSvgDataUrl = (category: string | undefined): string | null => {
  const cat = (category || 'default').toLowerCase();
  const iconFn = (icons as Record<string, any>)[cat];
  if (!iconFn) return null;
  // Usa cor branca - icon functions require (degrees, color) parameters
  const svg = iconFn(90, '#FFFFFF');
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
};

const getCategoryIconUrl = (category: string | undefined): string => {
  return mapIcons[mapIconKey(category)] || mapIcons.default || '/placeholder.svg';
};

const STREET_VIEW_WORKER = 'https://street-view.entrack-plataforma.workers.dev';
const GOOGLE_SV_API = 'https://maps.googleapis.com/maps/api/streetview';

const useStyles = makeStyles<{ tokens: ReturnType<typeof usePremiumTokens> }>()((theme, { tokens }) => ({
  // Root - Premium Glassmorphism Heavy Dock
  // 2ECOMP: PremiumUI Refresh — theme-aware background, border-top solid accent
  root: {
    position: 'relative',
    // Glassmorphism — theme-aware
    backgroundColor: theme.palette.mode === 'dark'
      ? 'rgba(15, 17, 22, 0.95)'
      : 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    border: `1px solid ${tokens?.border?.subtle || 'rgba(255, 255, 255, 0.12)'}`,
    // 2ECOMP: PremiumUI Refresh — border-top solid accent
    borderTop: `2px solid ${tokens.accent.primary}`,
    borderRadius: radius.xl,
    overflow: 'hidden',
    // Premium shadow
    boxShadow: `${tokens?.shadows?.lg || '0 8px 24px rgba(0, 0, 0, 0.5)'}, ${tokens?.shadows?.inset || 'inset 0 1px 0 rgba(255, 255, 255, 0.05)'}`,
    // Entrada animada
    animation: 'slideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  },

  // Header - Premium com glassmorphism light
  // 2ECOMP: PremiumUI Refresh — header compacto 36px
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 36,
    minHeight: 36,
    padding: '0 8px',
    background: tokens.surface.glass,
    borderBottom: `1px solid ${tokens.border.subtle}`,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  // Device icon - compacto
  deviceIconWrapper: {
    position: 'relative',
    width: 32,
    height: 32,
  },
  deviceIconBg: {
    width: '100%',
    height: '100%',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.action.selected,
  },
  deviceIcon: {
    width: 24,
    height: 24,
    filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.2))',
    transform: 'rotate(-12deg)',
  },
  // Small status dot indicator
  statusDotIndicator: {
    position: 'absolute',
    bottom: -1,
    right: -1,
    width: 9,
    height: 9,
    borderRadius: '50%',
    border: `2px solid ${theme.palette.background.paper}`,
    boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
  },
  deviceInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
  },
  deviceName: {
    fontFamily: typography.fontFamily.heading,
    fontWeight: 600,
    fontSize: '0.875rem',
    color: tokens.text.primary,
    lineHeight: 1.2,
    letterSpacing: '-0.01em',
  },
  // 2ECOMP: PremiumUI Refresh — status badge com border premium
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '2px 8px',
    borderRadius: radius.full,
    fontSize: '0.65rem',
    fontWeight: 700,
    fontFamily: typography.fontFamily.mono,
    width: 'fit-content',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.02em',
  },
  statusDot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
  },
  alarmBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '2px 8px',
    borderRadius: radius.full,
    fontSize: '0.65rem',
    fontWeight: 600,
    fontFamily: typography.fontFamily.mono,
    background: alpha(tokens.status.alarm, 0.15),
    color: tokens.status.alarm,
    animation: 'statusAlarm 0.8s ease-in-out infinite',
    textTransform: 'uppercase' as const,
  },
  '@keyframes alarmBlink': {
    '0%, 100%': {
      opacity: 1,
      background: 'rgba(244, 67, 54, 0.25)',
      boxShadow: '0 0 8px rgba(244, 67, 54, 0.5)',
    },
    '50%': {
      opacity: 0.5,
      background: 'rgba(244, 67, 54, 0.08)',
      boxShadow: 'none',
    },
  },

  // Action buttons - compactos
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
  },
  actionBtn: {
    width: 28,
    height: 28,
    borderRadius: radius.md,
    color: tokens.text.secondary,
    transition: transitions.all,
    '&:hover': {
      color: tokens.accent.primary,
      background: tokens.accent.subtle,
    },
  },
  actionBtnPrimary: {
    background: tokens.accent.primary,
    color: tokens.text.inverse,
    boxShadow: tokens.shadows.glowAccent,
    '&:hover': {
      background: tokens.accent.secondary,
      color: tokens.text.inverse,
    },
  },

  // Content - horizontal layout compacto
  // 2ECOMP: PremiumUI Refresh — overflow auto para 18+ sensors em 160px
  content: {
    display: 'flex',
    padding: theme.spacing(0.35, 0.75),
    gap: theme.spacing(0.75),
    alignItems: 'stretch',
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
    [theme.breakpoints.down('lg')]: {
      flexDirection: 'column',
      gap: theme.spacing(0.5),
    },
  },

  // Images container - Premium separador
  imagesContainer: {
    display: 'flex',
    gap: theme.spacing(0.5),
    flexShrink: 0,
    paddingRight: theme.spacing(1),
    borderRight: `1px solid ${tokens.border.subtle}`,
    marginRight: theme.spacing(0.5),
    [theme.breakpoints.down('lg')]: {
      borderRight: 'none',
      paddingRight: 0,
      marginRight: 0,
    },
  },
  imageBox: {
    width: 90,
    height: 65,
    borderRadius: radius.md,
    overflow: 'hidden',
    background: tokens.surface.glass,
    border: `1px solid ${tokens.border.subtle}`,
  },
  deviceImage: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
  streetViewBox: {
    width: 90,
    height: 65,
    borderRadius: radius.md,
    overflow: 'hidden',
    cursor: 'pointer',
    border: `1px solid ${tokens.border.subtle}`,
    transition: transitions.all,
    '&:hover': {
      borderColor: tokens.accent.primary,
      boxShadow: tokens.shadows.glowAccent,
    },
  },
  streetViewImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },

  // Info section - Premium com separador accent
  infoSection: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(0.15, 0.5),
    alignContent: 'center',
    width: 360,
    minWidth: 360,
    maxWidth: 360,
    flexShrink: 0,
    paddingRight: theme.spacing(1),
    borderRight: `1px solid ${tokens.border.subtle}`,
    marginRight: theme.spacing(1),
    overflow: 'hidden',
    [theme.breakpoints.down('lg')]: {
      width: '100%',
      minWidth: 'auto',
      maxWidth: '100%',
      borderRight: 'none',
      paddingRight: 0,
      marginRight: 0,
      borderBottom: `1px solid ${tokens.border.subtle}`,
      paddingBottom: theme.spacing(0.5),
      marginBottom: theme.spacing(0.5),
    },
  },
  infoItem: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(0.5),
    overflow: 'hidden',
    minWidth: 0,
  },
  infoItemFull: {
    gridColumn: '1 / -1',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    overflow: 'hidden',
    minWidth: 0,
    maxWidth: '100%',
  },
  infoLabel: {
    fontSize: '0.65rem',
    color: tokens.text.muted,
    whiteSpace: 'nowrap',
    textTransform: 'uppercase',
    letterSpacing: '0.02em',
    fontWeight: 500,
  },
  infoValue: {
    fontSize: '0.72rem',
    fontFamily: typography.fontFamily.mono,
    color: tokens.text.primary,
    fontWeight: 500,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
    minWidth: 0,
  },
  infoValueSuccess: { color: theme.palette.success.main },
  infoValueError: { color: theme.palette.error.main },
  infoValueInfo: { color: theme.palette.info.main },
  infoValueWithIcon: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
  },
  infoActionIcon: {
    fontSize: '0.85rem',
    color: theme.palette.primary.main,
    cursor: 'pointer',
    flexShrink: 0,
    '&:hover': { color: theme.palette.primary.dark },
  },

  // Sensors section - área delimitada
  sensorsSection: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    alignItems: 'flex-start',
    overflow: 'hidden',
  },
  sensorsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(105px, 1fr))',
    gap: theme.spacing(0.35),
    width: '100%',
  },

  // Sensor item - Premium glassmorphism light
  // 2ECOMP: PremiumUI Refresh — accent gradient sutil theme-aware
  sensorItem: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.5, 0.6),
    borderRadius: radius.md,
    background: theme.palette.mode === 'dark'
      ? `linear-gradient(135deg, rgba(0,212,170,0.05), ${tokens.surface.glass})`
      : `linear-gradient(135deg, rgba(0,212,170,0.03), ${tokens.surface.glass})`,
    border: `1px solid ${tokens.border.subtle}`,
    overflow: 'hidden',
    minHeight: 32,
    transition: transitions.all,
    '&:hover': {
      background: tokens.surface.glassHover,
      borderColor: tokens.border.default,
      transform: 'translateY(-1px)',
    },
  },
  sensorAccent: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 2,
    opacity: 0.8,
  },
  sensorIconWrapper: {
    width: 22,
    height: 22,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: radius.sm,
    flexShrink: 0,
  },
  sensorIcon: {
    fontSize: '0.8rem',
  },
  sensorContent: {
    minWidth: 0,
    flex: 1,
  },
  sensorLabel: {
    fontSize: '0.6rem',
    color: tokens.text.muted,
    lineHeight: 1,
    whiteSpace: 'nowrap',
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
    fontWeight: 500,
  },
  sensorValue: {
    fontSize: '0.72rem',
    fontWeight: 600,
    fontFamily: typography.fontFamily.mono,
    color: tokens.text.primary,
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
    letterSpacing: '-0.01em',
  },

  // Colors
  success: { color: theme.palette.success.main },
  error: { color: theme.palette.error.main },
  warning: { color: theme.palette.warning.main },
  info: { color: theme.palette.info.main },
  neutral: { color: theme.palette.text.secondary },
}));

interface DeviceDetailPanelPremiumProps {
  device: any;
  position: any;
  onClose: () => void;
  onLocate?: () => void;
  onHistory?: () => void;
  onCommand?: () => void;
  onEdit?: () => void;
  onLockUnlock?: () => void;
  onAnchor?: () => void;
  onCamera?: () => void;
  onDelete?: () => void;
  onDetails?: () => void;
  onShare?: () => void;
  onGeofence?: () => void;
  cameraLoading?: boolean;
}

const DeviceDetailPanelPremium: React.FC<DeviceDetailPanelPremiumProps> = memo(({
  device, position, onClose, onLocate, onHistory, onCommand, onEdit,
  onLockUnlock, onAnchor, onCamera, onDelete, onDetails, onShare, onGeofence,
  cameraLoading = false,
}) => {
  const tokens = usePremiumTokens();
  const { classes, cx } = useStyles({ tokens });
  const dispatch = useDispatch(); // 2ECOMP: follow popup
  const t = useTranslation();
  const speedUnit = useAttributePreference('speedUnit', 'kn');

  // 2ECOMP: follow popup — estado do popup
  const followPopupDeviceIds = useSelector(
    (state: any) => state.devices.followPopupDeviceIds,
  );
  const isFollowingDevice = followPopupDeviceIds?.includes(device?.id);
  const positionAttributes = usePositionAttributes(t);
  const positionItems = useAttributePreference('positionItems', 'fixTime,address,speed,totalDistance,blocked,ignition');

  const readonly = useRestriction('readonly');
  const isAdmin = useAdministrator();
  const isManager = useManager();
  const deviceReadonly = useDeviceReadonly();
  const limitCommands = useLimitCommand();

  const shareDisabled = useSelector((state: any) => state.session.server?.attributes?.disableShare);
  const user = useSelector((state: any) => state.session.user);

  const [streetViewAvailable, setStreetViewAvailable] = useState(false);
  const [streetViewUrl, setStreetViewUrl] = useState('');
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const googleKey = useAttributePreference('googleKey', '');

  // Street View validation — uses Image() to bypass CORS (fetch fails on referrer-restricted keys)
  useEffect(() => {
    if (!position?.latitude || !position?.longitude) {
      setStreetViewAvailable(false);
      return;
    }
    let cancelled = false;
    const params = `heading=${position.course || 0}&location=${position.latitude},${position.longitude}&size=288x144&return_error_code=true`;

    const workerUrl = `${STREET_VIEW_WORKER}/?${params}`;
    const img = new Image();
    img.onload = () => { if (!cancelled) { setStreetViewUrl(workerUrl); setStreetViewAvailable(true); } };
    img.onerror = () => {
      if (cancelled) return;
      if (googleKey) {
        const gUrl = `${GOOGLE_SV_API}?${params}&key=${googleKey}`;
        const img2 = new Image();
        img2.onload = () => { if (!cancelled) { setStreetViewUrl(gUrl); setStreetViewAvailable(true); } };
        img2.onerror = () => { if (!cancelled) { setStreetViewAvailable(false); setStreetViewUrl(''); } };
        img2.src = gUrl;
      } else {
        setStreetViewAvailable(false);
      }
    };
    img.src = workerUrl;

    return () => { cancelled = true; };
  }, [position?.latitude, position?.longitude, position?.course, googleKey]);

  if (!device) return null;

  const isOnline = device.status === 'online';
  const attrs = position?.attributes || {};
  const isMoving = attrs.motion === true;

  // Extract values - Existing
  const ignition = attrs.ignition;
  const motion = attrs.motion;
  const batteryLevel = attrs.batteryLevel;
  const satellites = attrs.sat ?? 0;
  const odometer = attrs.odometer ? Math.round(attrs.odometer / 1000) : (attrs.totalDistance ? Math.round(attrs.totalDistance / 1000) : 0);
  const hours = attrs.hours ? (attrs.hours / 3600000).toFixed(1) : '0';
  const power = getPowerVoltage(attrs);
  const rssi = attrs.rssi ?? '-';
  const deviceImage = device.attributes?.deviceImage;
  const alarm = attrs.alarm;
  const speed = position?.speed || 0;

  // Extract values - New 7 cards
  const fuel = attrs.fuel ?? null;
  const rpm = attrs.rpm ?? null;
  const coolantTemp = attrs.coolantTemp ?? attrs.engineTemp ?? null;
  const driverUniqueId = attrs.driverUniqueId ?? null;
  const course = position?.course ?? null;
  const drivingTime = attrs.drivingTime ? (attrs.drivingTime / 3600000).toFixed(1) : null;
  const altitude = position?.altitude ?? null;

  const getBlockedState = (): boolean => {
    if (!position?.attributes) return false;
    const val = position.attributes.blocked;
    if (typeof val === 'boolean') return val;
    if (typeof val === 'string') return val.toLowerCase() === 'true' || val === '1';
    if (typeof val === 'number') return val === 1;
    return false;
  };
  const blocked = getBlockedState();

  const hasAnchor = Boolean(device?.attributes?.hasAnchor && device.attributes.hasAnchor !== '' && device.attributes.hasAnchor !== 'null');
  const lockOnExit = device.attributes?.lockOnExit;

  const BatteryIcon = batteryLevel > 80 ? BatteryFull : batteryLevel > 50 ? Battery80 : batteryLevel > 20 ? Battery50 : Battery20;

  const handleStreetView = () => {
    if (position) {
      window.open(`https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${position.latitude}%2C${position.longitude}&heading=${position.course || 0}`, '_blank');
    }
  };

  // Premium status colors
  const statusColor = isMoving
    ? tokens.status.moving
    : (isOnline ? tokens.status.online : tokens.status.offline);
  const statusText = isMoving
    ? (t('eventDeviceMoving') || 'Moving')
    : (isOnline ? (t('deviceStatusOnline') || 'Online') : (t('deviceStatusOffline') || 'Offline'));

  const deviceIconSrc = useMemo(() => {
    const local = getLocalSvgDataUrl(device.category);
    if (local) return local;
    const remote = getIcon3dUrl(device.category);
    if (remote) return remote;
    return getCategoryIconUrl(device.category);
  }, [device.category]);

  // Gradient colors for sensor accents - Premium palette (theme-aware)
  const getAccentGradient = (type: 'success' | 'error' | 'warning' | 'info' | 'neutral' | 'purple' | 'accent') => {
    const colors: Record<string, string> = {
      success: `linear-gradient(90deg, ${tokens.semantic.success}, ${tokens.accent.secondary})`,
      error: `linear-gradient(90deg, ${tokens.semantic.error}, ${tokens.semantic.error}99)`,
      warning: `linear-gradient(90deg, ${tokens.semantic.warning}, ${tokens.semantic.warning}99)`,
      info: `linear-gradient(90deg, ${tokens.semantic.info}, ${tokens.semantic.info}99)`,
      neutral: `linear-gradient(90deg, ${tokens.semantic.neutral}, ${tokens.semantic.neutral}99)`,
      purple: `linear-gradient(90deg, ${tokens.semantic.purple}, ${tokens.semantic.purple}99)`,
      accent: `linear-gradient(90deg, ${tokens.accent.primary}, ${tokens.accent.secondary})`,
    };
    return colors[type] || colors.neutral;
  };

  // Helper to get semantic background color (theme-aware)
  const getSemanticBg = (type: 'success' | 'error' | 'warning' | 'info' | 'neutral' | 'purple') => {
    const bgs: Record<string, string> = {
      success: tokens.semantic.successBg,
      error: tokens.semantic.errorBg,
      warning: tokens.semantic.warningBg,
      info: tokens.semantic.infoBg,
      neutral: tokens.semantic.neutralBg,
      purple: tokens.semantic.purpleBg,
    };
    return bgs[type] || bgs.neutral;
  };

  // Helper to get semantic color (theme-aware)
  const getSemanticColor = (type: 'success' | 'error' | 'warning' | 'info' | 'neutral' | 'purple') => {
    const colors: Record<string, string> = {
      success: tokens.semantic.success,
      error: tokens.semantic.error,
      warning: tokens.semantic.warning,
      info: tokens.semantic.info,
      neutral: tokens.semantic.neutral,
      purple: tokens.semantic.purple,
    };
    return colors[type] || colors.neutral;
  };

  return (
    <Paper className={classes.root} elevation={0}>
      {/* Header */}
      <Box className={classes.header}>
        <Box className={classes.headerLeft}>
          {/* Device icon with status dot */}
          <Box className={classes.deviceIconWrapper}>
            <Box className={classes.deviceIconBg}>
              <img src={deviceIconSrc} alt={device.category || 'device'} className={classes.deviceIcon} />
            </Box>
            <Box className={classes.statusDotIndicator} sx={{ backgroundColor: statusColor }} />
          </Box>
          <Box className={classes.deviceInfo}>
            <Typography className={classes.deviceName}>{device.name}</Typography>
            {/* 2ECOMP: PremiumUI Refresh — status badge com border */}
            <Box className={classes.statusBadge} sx={{ background: alpha(statusColor, 0.15), color: statusColor, border: `1px solid ${alpha(statusColor, 0.25)}` }}>
              <Box className={classes.statusDot} sx={{ background: statusColor }} />
              {statusText}
            </Box>
            {alarm && (
              <Box className={classes.alarmBadge}>
                <NotificationsActive sx={{ fontSize: 12 }} />
                {formatAlarm(alarm, t)}
              </Box>
            )}
          </Box>
        </Box>

        {/* Actions */}
        <Box className={classes.headerActions}>
          <Tooltip title={t('mapLiveRoutes') || 'Localizar'}>
            <IconButton className={cx(classes.actionBtn, classes.actionBtnPrimary)} size="small" onClick={onLocate}>
              <MyLocation sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Street View">
            <IconButton className={classes.actionBtn} size="small" onClick={handleStreetView}>
              <StreetviewOutlined sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('reportReplay') || 'Historico'}>
            <IconButton className={classes.actionBtn} size="small" onClick={onHistory}>
              <Route sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          {(isAdmin || isManager) && (
            <Tooltip title={t('commandTitle') || 'Comando'}>
              <IconButton className={classes.actionBtn} size="small" onClick={onCommand} disabled={deviceReadonly}>
                <Send sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
          {(isAdmin || isManager || limitCommands) && (
            <Tooltip title={blocked ? t('commandEngineResume') || 'Desbloquear' : t('commandEngineStop') || 'Bloquear'}>
              <IconButton className={classes.actionBtn} size="small" onClick={onLockUnlock} disabled={deviceReadonly}>
                {blocked ? <Lock sx={{ fontSize: 16, color: 'error.main' }} /> : <LockOpen sx={{ fontSize: 16, color: 'success.main' }} />}
              </IconButton>
            </Tooltip>
          )}
          {(isAdmin || isManager || limitCommands) && (
            <Tooltip title={hasAnchor ? 'Ancora Ativa' : 'Ancora'}>
              <IconButton className={classes.actionBtn} size="small" onClick={onAnchor} disabled={deviceReadonly}>
                <Anchor sx={{ fontSize: 16, color: hasAnchor ? (lockOnExit ? 'error.main' : 'success.main') : 'inherit' }} />
              </IconButton>
            </Tooltip>
          )}
          {(isAdmin || isManager) && (
            <Tooltip title={t('sharedEdit') || 'Editar'}>
              <IconButton className={classes.actionBtn} size="small" onClick={onEdit} disabled={deviceReadonly}>
                <Edit sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={t('camera') || 'Camera'}>
            <IconButton className={classes.actionBtn} size="small" onClick={onCamera} disabled={cameraLoading}>
              <CameraAlt sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          {(isAdmin || isManager) && onShare && !shareDisabled && !user?.temporary && (
            <Tooltip title={t('deviceShare') || 'Compartilhar'}>
              <IconButton className={classes.actionBtn} size="small" onClick={onShare}>
                <PersonAdd sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={t('sharedExtra') || 'Mais'}>
            <IconButton className={classes.actionBtn} size="small" onClick={(e) => setMenuAnchor(e.currentTarget)}>
              <MoreVert sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          {(isAdmin || isManager) && (
            <Tooltip title={t('sharedRemove') || 'Excluir'}>
              <IconButton className={classes.actionBtn} size="small" onClick={onDelete} disabled={deviceReadonly} sx={{ '&:hover': { color: 'error.main' } }}>
                <Delete sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={t('sharedClose') || 'Fechar'}>
            <IconButton className={classes.actionBtn} size="small" onClick={onClose}>
              <Close sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Menu */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        {/* 2ECOMP: follow popup — Seguir/Parar de seguir */}
        <MenuItem onClick={() => {
          setMenuAnchor(null);
          if (isFollowingDevice) {
            dispatch(devicesActions.removeFollowPopup(device.id));
          } else {
            dispatch(devicesActions.addFollowPopup(device.id));
          }
        }}>
          <Navigation sx={{ mr: 1, fontSize: 16, color: isFollowingDevice ? '#10b981' : undefined }} />
          {isFollowingDevice ? (t('deviceStopFollow') || 'Parar de seguir') : (t('deviceFollow') || 'Seguir')}
        </MenuItem>
        {onDetails && (
          <MenuItem onClick={() => { setMenuAnchor(null); onDetails(); }}>
            <Info sx={{ mr: 1, fontSize: 16 }} /> {t('sharedInfoTitle') || 'Detalhes'}
          </MenuItem>
        )}
        {!readonly && onGeofence && (
          <MenuItem onClick={() => { setMenuAnchor(null); onGeofence(); }}>
            <AddLocationAlt sx={{ mr: 1, fontSize: 16 }} /> {t('sharedCreateGeofence') || 'Criar Geocerca'}
          </MenuItem>
        )}
        <Divider />
        {position && [
          <MenuItem key="google" component="a" href={`https://www.google.com/maps/search/?api=1&query=${position.latitude}%2C${position.longitude}`} target="_blank">
            <Map sx={{ mr: 1, fontSize: 16 }} /> Google Maps
          </MenuItem>,
          <MenuItem key="apple" component="a" href={`http://maps.apple.com/?ll=${position.latitude},${position.longitude}`} target="_blank">
            <Map sx={{ mr: 1, fontSize: 16 }} /> Apple Maps
          </MenuItem>,
        ]}
      </Menu>

      {/* Content */}
        <Box className={classes.content}>
          {/* Images */}
          <Box className={classes.imagesContainer}>
            {/* Imagem realistica comentada para dar mais espaco ao content
            <Box className={classes.imageBox}>
              <img
                src={deviceImage ? `/api/media/${device.uniqueId}/${deviceImage}` : getRealisticImage(device.category)}
                alt={device.name}
                className={classes.deviceImage}
              />
            </Box>
            */}
            {streetViewAvailable && position && (
              <Tooltip title="Street View">
                <Box className={classes.streetViewBox} onClick={handleStreetView}>
                  <img
                    src={streetViewUrl.replace('288x144', '100x75')}
                    alt="Street View"
                    className={classes.streetViewImg}
                  />
                </Box>
              </Tooltip>
            )}
          </Box>

          {/* Info Section */}
          <Box className={classes.infoSection}>
            {position && positionItems.split(',')
              .filter((key: string) => position.hasOwnProperty(key) || position.attributes?.hasOwnProperty(key))
              .map((key: string) => {
                const getColorClass = () => {
                  const val = position[key] ?? position.attributes?.[key];
                  if (key === 'blocked') return val ? classes.infoValueError : classes.infoValueSuccess;
                  if (key === 'ignition') return val ? classes.infoValueSuccess : classes.infoValueError;
                  if (key === 'motion') return val ? classes.infoValueInfo : '';
                  if (key === 'speed' && (position.speed || 0) > 0) return classes.infoValueInfo;
                  return '';
                };
                const isAddressField = key === 'address';
                const isFullWidthField = ['fixTime', 'deviceTime', 'serverTime', 'totalDistance', 'address'].includes(key);
                return (
                  <Box key={key} className={isFullWidthField ? classes.infoItemFull : classes.infoItem}>
                    <Typography className={classes.infoLabel}>{(positionAttributes as any)[key]?.name}:</Typography>
                    {isAddressField ? (
                      <Box className={classes.infoValueWithIcon}>
                        <Typography className={classes.infoValue} title={position.address || ''}>
                          <PositionValue position={position} property="address" attribute={null} />
                        </Typography>
                        <Tooltip title="Google Maps">
                          <Visibility className={classes.infoActionIcon} onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${position.latitude},${position.longitude}`, '_blank')} />
                        </Tooltip>
                      </Box>
                    ) : (
                      <Typography
                        className={cx(classes.infoValue, getColorClass())}
                        sx={isFullWidthField ? { whiteSpace: 'nowrap', overflow: 'visible' } : undefined}
                      >
                        <PositionValue position={position} property={position.hasOwnProperty(key) ? key : null} attribute={position.hasOwnProperty(key) ? null : key} />
                      </Typography>
                    )}
                  </Box>
                );
              })}
          </Box>

          {/* Sensors Grid */}
          <Box className={classes.sensorsSection}>
            <Box className={classes.sensorsGrid}>
              {/* Odometer */}
              <Box className={classes.sensorItem}>
                <Box className={classes.sensorAccent} sx={{ background: getAccentGradient('purple') }} />
                <Box className={classes.sensorIconWrapper} sx={{ backgroundColor: getSemanticBg('purple') }}>
                  <FontAwesomeIcon icon={faTachometerAlt} className={classes.sensorIcon} style={{ color: getSemanticColor('purple') }} />
                </Box>
                <Box className={classes.sensorContent}>
                  <Typography className={classes.sensorLabel}>Odômetro</Typography>
                  <Typography className={classes.sensorValue}>{odometer.toLocaleString()} km</Typography>
                </Box>
              </Box>

              {/* Battery */}
              <Box className={classes.sensorItem}>
                <Box className={classes.sensorAccent} sx={{ background: getAccentGradient(batteryLevel > 50 ? 'success' : batteryLevel > 20 ? 'warning' : 'error') }} />
                <Box className={classes.sensorIconWrapper} sx={{ backgroundColor: getSemanticBg(batteryLevel > 20 ? 'success' : 'error') }}>
                  <BatteryIcon className={classes.sensorIcon} sx={{ color: batteryLevel > 20 ? 'success.main' : 'error.main' }} />
                </Box>
                <Box className={classes.sensorContent}>
                  <Typography className={classes.sensorLabel}>Bateria</Typography>
                  <Typography className={classes.sensorValue}>{batteryLevel ?? '-'}%</Typography>
                </Box>
              </Box>

              {/* Power */}
              <Box className={classes.sensorItem}>
                <Box className={classes.sensorAccent} sx={{ background: getAccentGradient(Number(power) > 11 ? 'success' : 'warning') }} />
                <Box className={classes.sensorIconWrapper} sx={{ backgroundColor: getSemanticBg(Number(power) > 11 ? 'success' : 'warning') }}>
                  <FontAwesomeIcon icon={faCarBattery} className={classes.sensorIcon} style={{ color: getSemanticColor(Number(power) > 11 ? 'success' : 'warning') }} />
                </Box>
                <Box className={classes.sensorContent}>
                  <Typography className={classes.sensorLabel}>Tensão</Typography>
                  <Typography className={classes.sensorValue}>{power} V</Typography>
                </Box>
              </Box>

              {/* Satellites */}
              <Box className={classes.sensorItem}>
                <Box className={classes.sensorAccent} sx={{ background: getAccentGradient(satellites > 8 ? 'success' : satellites > 4 ? 'warning' : 'error') }} />
                <Box className={classes.sensorIconWrapper} sx={{ backgroundColor: getSemanticBg(satellites > 4 ? 'success' : 'error') }}>
                  <SatelliteAlt className={classes.sensorIcon} sx={{ color: satellites > 4 ? 'success.main' : 'error.main' }} />
                </Box>
                <Box className={classes.sensorContent}>
                  <Typography className={classes.sensorLabel}>Satélites</Typography>
                  <Typography className={classes.sensorValue}>{satellites}</Typography>
                </Box>
              </Box>

              {/* Ignition */}
              <Box className={classes.sensorItem}>
                <Box className={classes.sensorAccent} sx={{ background: getAccentGradient(ignition ? 'success' : 'error') }} />
                <Box className={classes.sensorIconWrapper} sx={{ backgroundColor: getSemanticBg(ignition ? 'success' : 'error') }}>
                  <Power className={classes.sensorIcon} sx={{ color: ignition ? 'success.main' : 'error.main' }} />
                </Box>
                <Box className={classes.sensorContent}>
                  <Typography className={classes.sensorLabel}>Ignição</Typography>
                  <Typography className={classes.sensorValue} sx={{ color: ignition ? 'success.main' : 'error.main' }}>{ignition ? 'Ligada' : 'Deslig.'}</Typography>
                </Box>
              </Box>

              {/* Speed */}
              <Box className={classes.sensorItem}>
                <Box className={classes.sensorAccent} sx={{ background: getAccentGradient(speed > 0 ? 'info' : 'neutral') }} />
                <Box className={classes.sensorIconWrapper} sx={{ backgroundColor: getSemanticBg(speed > 0 ? 'info' : 'neutral') }}>
                  <Speed className={classes.sensorIcon} sx={{ color: speed > 0 ? 'info.main' : 'text.secondary' }} />
                </Box>
                <Box className={classes.sensorContent}>
                  <Typography className={classes.sensorLabel}>Velocidade</Typography>
                  <Typography className={classes.sensorValue} sx={{ color: speed > 0 ? 'info.main' : 'inherit' }}>{formatSpeed(speed, speedUnit, t)}</Typography>
                </Box>
              </Box>

              {/* RSSI */}
              <Box className={classes.sensorItem}>
                <Box className={classes.sensorAccent} sx={{ background: getAccentGradient('neutral') }} />
                <Box className={classes.sensorIconWrapper} sx={{ backgroundColor: getSemanticBg('neutral') }}>
                  <SignalCellularAlt className={classes.sensorIcon} sx={{ color: 'text.secondary' }} />
                </Box>
                <Box className={classes.sensorContent}>
                  <Typography className={classes.sensorLabel}>Sinal RSSI</Typography>
                  <Typography className={classes.sensorValue}>{rssi}%</Typography>
                </Box>
              </Box>

              {/* Hours */}
              <Box className={classes.sensorItem}>
                <Box className={classes.sensorAccent} sx={{ background: getAccentGradient('neutral') }} />
                <Box className={classes.sensorIconWrapper} sx={{ backgroundColor: getSemanticBg('neutral') }}>
                  <FontAwesomeIcon icon={faHourglassHalf} className={classes.sensorIcon} style={{ color: getSemanticColor('neutral') }} />
                </Box>
                <Box className={classes.sensorContent}>
                  <Typography className={classes.sensorLabel}>Horímetro</Typography>
                  <Typography className={classes.sensorValue}>{hours} h</Typography>
                </Box>
              </Box>

              {/* Motion */}
              <Box className={classes.sensorItem}>
                <Box className={classes.sensorAccent} sx={{ background: getAccentGradient(motion ? 'info' : 'neutral') }} />
                <Box className={classes.sensorIconWrapper} sx={{ backgroundColor: getSemanticBg(motion ? 'info' : 'neutral') }}>
                  <DirectionsCar className={classes.sensorIcon} sx={{ color: motion ? 'info.main' : 'text.secondary' }} />
                </Box>
                <Box className={classes.sensorContent}>
                  <Typography className={classes.sensorLabel}>Movimento</Typography>
                  <Typography className={classes.sensorValue} sx={{ color: motion ? 'info.main' : 'inherit' }}>{motion ? 'Sim' : 'Não'}</Typography>
                </Box>
              </Box>

              {/* Blocked */}
              <Box className={classes.sensorItem}>
                <Box className={classes.sensorAccent} sx={{ background: getAccentGradient(blocked ? 'error' : 'success') }} />
                <Box className={classes.sensorIconWrapper} sx={{ backgroundColor: getSemanticBg(blocked ? 'error' : 'success') }}>
                  {blocked ? <Lock className={classes.sensorIcon} sx={{ color: 'error.main' }} /> : <LockOpen className={classes.sensorIcon} sx={{ color: 'success.main' }} />}
                </Box>
                <Box className={classes.sensorContent}>
                  <Typography className={classes.sensorLabel}>Bloqueio</Typography>
                  <Typography className={classes.sensorValue} sx={{ color: blocked ? 'error.main' : 'success.main' }}>{blocked ? 'Ativo' : 'Livre'}</Typography>
                </Box>
              </Box>

              {/* Anchor */}
              <Box className={classes.sensorItem}>
                <Box className={classes.sensorAccent} sx={{ background: getAccentGradient(hasAnchor ? (lockOnExit ? 'error' : 'success') : 'neutral') }} />
                <Box className={classes.sensorIconWrapper} sx={{ backgroundColor: getSemanticBg(hasAnchor ? (lockOnExit ? 'error' : 'success') : 'neutral') }}>
                  <Anchor className={classes.sensorIcon} sx={{ color: hasAnchor ? (lockOnExit ? 'error.main' : 'success.main') : 'text.secondary' }} />
                </Box>
                <Box className={classes.sensorContent}>
                  <Typography className={classes.sensorLabel}>Âncora</Typography>
                  <Typography className={classes.sensorValue} sx={{ color: hasAnchor ? (lockOnExit ? 'error.main' : 'success.main') : 'inherit' }}>
                    {hasAnchor ? (lockOnExit ? 'Bloqueia' : 'Ativa') : 'Não'}
                  </Typography>
                </Box>
              </Box>

              {/* NEW CARDS - Fuel */}
              <Box className={classes.sensorItem}>
                <Box className={classes.sensorAccent} sx={{ background: getAccentGradient(fuel !== null && fuel > 20 ? 'success' : fuel !== null ? 'warning' : 'neutral') }} />
                <Box className={classes.sensorIconWrapper} sx={{ backgroundColor: getSemanticBg(fuel !== null && fuel > 20 ? 'success' : fuel !== null ? 'warning' : 'neutral') }}>
                  <LocalGasStation className={classes.sensorIcon} sx={{ color: fuel !== null && fuel > 20 ? 'success.main' : fuel !== null ? 'warning.main' : 'text.secondary' }} />
                </Box>
                <Box className={classes.sensorContent}>
                  <Typography className={classes.sensorLabel}>Combustível</Typography>
                  <Typography className={classes.sensorValue}>{fuel !== null ? `${fuel} L` : '-'}</Typography>
                </Box>
              </Box>

              {/* RPM */}
              <Box className={classes.sensorItem}>
                <Box className={classes.sensorAccent} sx={{ background: getAccentGradient(rpm !== null && rpm > 0 ? 'info' : 'neutral') }} />
                <Box className={classes.sensorIconWrapper} sx={{ backgroundColor: getSemanticBg(rpm !== null && rpm > 0 ? 'info' : 'neutral') }}>
                  <FontAwesomeIcon icon={faGaugeHigh} className={classes.sensorIcon} style={{ color: getSemanticColor(rpm !== null && rpm > 0 ? 'info' : 'neutral') }} />
                </Box>
                <Box className={classes.sensorContent}>
                  <Typography className={classes.sensorLabel}>RPM</Typography>
                  <Typography className={classes.sensorValue} sx={{ color: rpm !== null && rpm > 3000 ? 'warning.main' : 'inherit' }}>{rpm !== null ? rpm.toLocaleString() : '-'}</Typography>
                </Box>
              </Box>

              {/* Coolant Temp */}
              <Box className={classes.sensorItem}>
                <Box className={classes.sensorAccent} sx={{ background: getAccentGradient(coolantTemp !== null && coolantTemp > 100 ? 'error' : coolantTemp !== null ? 'success' : 'neutral') }} />
                <Box className={classes.sensorIconWrapper} sx={{ backgroundColor: getSemanticBg(coolantTemp !== null && coolantTemp > 100 ? 'error' : coolantTemp !== null ? 'success' : 'neutral') }}>
                  <Thermostat className={classes.sensorIcon} sx={{ color: coolantTemp !== null && coolantTemp > 100 ? 'error.main' : coolantTemp !== null ? 'success.main' : 'text.secondary' }} />
                </Box>
                <Box className={classes.sensorContent}>
                  <Typography className={classes.sensorLabel}>Temp. Motor</Typography>
                  <Typography className={classes.sensorValue} sx={{ color: coolantTemp !== null && coolantTemp > 100 ? 'error.main' : 'inherit' }}>{coolantTemp !== null ? `${coolantTemp}°C` : '-'}</Typography>
                </Box>
              </Box>

              {/* Driver */}
              <Box className={classes.sensorItem}>
                <Box className={classes.sensorAccent} sx={{ background: getAccentGradient(driverUniqueId ? 'success' : 'neutral') }} />
                <Box className={classes.sensorIconWrapper} sx={{ backgroundColor: getSemanticBg(driverUniqueId ? 'success' : 'neutral') }}>
                  <Person className={classes.sensorIcon} sx={{ color: driverUniqueId ? 'success.main' : 'text.secondary' }} />
                </Box>
                <Box className={classes.sensorContent}>
                  <Typography className={classes.sensorLabel}>Motorista</Typography>
                  <Typography className={classes.sensorValue} sx={{ fontSize: '0.65rem' }}>{driverUniqueId || '-'}</Typography>
                </Box>
              </Box>

              {/* Course/Direction */}
              <Box className={classes.sensorItem}>
                <Box className={classes.sensorAccent} sx={{ background: getAccentGradient(course !== null ? 'info' : 'neutral') }} />
                <Box className={classes.sensorIconWrapper} sx={{ backgroundColor: getSemanticBg(course !== null ? 'info' : 'neutral') }}>
                  <Explore className={classes.sensorIcon} sx={{ color: course !== null ? 'info.main' : 'text.secondary', transform: course !== null ? `rotate(${course}deg)` : 'none' }} />
                </Box>
                <Box className={classes.sensorContent}>
                  <Typography className={classes.sensorLabel}>Direção</Typography>
                  <Typography className={classes.sensorValue}>{course !== null ? `${Math.round(course)}°` : '-'}</Typography>
                </Box>
              </Box>

              {/* Driving Time */}
              <Box className={classes.sensorItem}>
                <Box className={classes.sensorAccent} sx={{ background: getAccentGradient(drivingTime !== null && parseFloat(drivingTime) > 8 ? 'warning' : drivingTime !== null ? 'success' : 'neutral') }} />
                <Box className={classes.sensorIconWrapper} sx={{ backgroundColor: getSemanticBg(drivingTime !== null && parseFloat(drivingTime) > 8 ? 'warning' : drivingTime !== null ? 'success' : 'neutral') }}>
                  <Timer className={classes.sensorIcon} sx={{ color: drivingTime !== null && parseFloat(drivingTime) > 8 ? 'warning.main' : drivingTime !== null ? 'success.main' : 'text.secondary' }} />
                </Box>
                <Box className={classes.sensorContent}>
                  <Typography className={classes.sensorLabel}>T. Direção</Typography>
                  <Typography className={classes.sensorValue} sx={{ color: drivingTime !== null && parseFloat(drivingTime) > 8 ? 'warning.main' : 'inherit' }}>{drivingTime !== null ? `${drivingTime} h` : '-'}</Typography>
                </Box>
              </Box>

              {/* Altitude */}
              <Box className={classes.sensorItem}>
                <Box className={classes.sensorAccent} sx={{ background: getAccentGradient(altitude !== null ? 'info' : 'neutral') }} />
                <Box className={classes.sensorIconWrapper} sx={{ backgroundColor: getSemanticBg(altitude !== null ? 'info' : 'neutral') }}>
                  <Terrain className={classes.sensorIcon} sx={{ color: altitude !== null ? 'info.main' : 'text.secondary' }} />
                </Box>
                <Box className={classes.sensorContent}>
                  <Typography className={classes.sensorLabel}>Altitude</Typography>
                  <Typography className={classes.sensorValue}>{altitude !== null ? `${Math.round(altitude)} m` : '-'}</Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
    </Paper>
  );
});

DeviceDetailPanelPremium.displayName = 'DeviceDetailPanelPremium';

export default DeviceDetailPanelPremium;
